import React, { useEffect, useState } from 'react';
import axios from 'axios';
import toast from 'react-hot-toast';
import {
    ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid,
    LineChart, Line, PieChart, Pie, Cell, Legend, AreaChart, Area,
} from 'recharts';
import { formatCurrency } from '../utils/currency';
import {
    BarChart3, Users, DollarSign, Home, Zap, Wrench, Building2,
    TrendingUp, TrendingDown, Percent, AlertCircle, ShieldCheck, UserCog,
} from 'lucide-react';
import { PageHeader, Card, StatTile, Tabs, DataTable, Badge, EmptyState } from '../components/ui';
import { useAuth } from '../contexts/AuthContext';

const COLORS = {
    income:      '#0f766e',   // primary-700
    expenses:    '#f59e0b',   // warning-500
    net:         '#22c55e',   // success-500
    accent:      '#0369a1',   // accent-700
    grid:        '#e2e8f0',
    axis:        '#94a3b8',
    occupied:    '#0f766e',
    available:   '#cbd5e1',
    maintenance: '#f59e0b',
    unavailable: '#64748b',
};

const compact = v => {
    const n = Number(v || 0);
    if (Math.abs(n) >= 1_000_000) return (n / 1_000_000).toFixed(1) + 'M';
    if (Math.abs(n) >= 1_000)     return (n / 1_000).toFixed(0) + 'k';
    return String(n);
};

const Reports = () => {
    const { user } = useAuth();
    const [tab, setTab] = useState('overview');
    return (
        <div className="animate-fade-in">
            <PageHeader
                title="Reports"
                subtitle="Portfolio analytics across finance, tenants, buildings, and operations."
            />
            <Tabs
                activeId={tab}
                onChange={setTab}
                tabs={[
                    { id: 'overview',    label: 'Overview',    icon: BarChart3 },
                    { id: 'financial',   label: 'Financial',   icon: DollarSign },
                    { id: 'tenants',     label: 'Tenants',     icon: Users },
                    ...(user?.user_level==='admin'?[{ id: 'managers', label: 'Managers',icon:UserCog }]:[]),
                    { id: 'buildings',   label: 'Buildings',   icon: Building2 },
                    { id: 'balances',    label: 'Balances',    icon: AlertCircle },
                    { id: 'utilities',   label: 'Utilities',   icon: Zap },
                    { id: 'maintenance', label: 'Maintenance', icon: Wrench },
                ]}
            >
                {(active) => (
                    <div className="pt-6">
                        {active === 'overview'    && <OverviewTab />}
                        {active === 'financial'   && <FinancialTab />}
                        {active === 'tenants'     && <TenantsTab />}
                        {active === 'managers'    && <ManagersTab />}
                        {active === 'buildings'   && <BuildingsTab />}
                        {active === 'balances'    && <BalancesTab />}
                        {active === 'utilities'   && <UtilitiesTab />}
                        {active === 'maintenance' && <MaintenanceTab />}
                    </div>
                )}
            </Tabs>
        </div>
    );
};

/* ============================================================
 * OVERVIEW
 * ============================================================ */
const OverviewTab = () => {
    const [d, setD] = useState(null);
    useEffect(() => { axios.get('/api/reports/overview').then(r => setD(r.data)).catch(() => toast.error('Failed')); }, []);
    if (!d) return <LoadingSkeleton />;
    return (
        <div className="space-y-6">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 sm:gap-4">
                <StatTile label="Income MTD"       value={formatCurrency(d.income)}      icon={TrendingUp}  accent="success" />
                <StatTile label="Expenses MTD"     value={formatCurrency(d.expenses)}    icon={TrendingDown} accent="warning" />
                <StatTile label="Net"              value={formatCurrency(d.net)}         accent={d.net >= 0 ? 'success' : 'danger'} />
                <StatTile label="Outstanding"      value={formatCurrency(d.outstanding)} icon={AlertCircle}  accent="warning" hint="Across portfolio" />
                <StatTile label="Collection rate"  value={d.collection_rate != null ? `${d.collection_rate}%` : '—'} icon={Percent} accent="primary" hint="Paid vs billed (MTD)" />
                <StatTile label="Occupancy"        value={d.occupancy?.rate != null ? `${d.occupancy.rate}%` : '—'}
                          icon={Home} accent="primary" hint={`${d.occupancy?.occupied || 0}/${d.occupancy?.total || 0} units`} />
                <StatTile label="Billed MTD"       value={formatCurrency(d.billed)}    accent="primary" />
                <StatTile label="Collected MTD"    value={formatCurrency(d.collected)} accent="success" />
            </div>
        </div>
    );
};

/* ============================================================
 * FINANCIAL
 * ============================================================ */
const FinancialTab = () => {
    const [trend, setTrend] = useState([]);
    const [pnl, setPnl]     = useState([]);
    useEffect(() => {
        Promise.all([
            axios.get('/api/reports/trend?months=12'),
            axios.get('/api/reports/property-pnl'),
        ]).then(([t, p]) => {
            setTrend(t.data.months || []);
            setPnl((p.data.properties || []).sort((a,b) => b.net - a.net));
        }).catch(() => toast.error('Failed'));
    }, []);
    return (
        <div className="space-y-6">
            <Card title="Income vs Expenses" subtitle="Last 12 months">
                {trend.length === 0 ? <Empty msg="No data yet." /> : (
                    <ResponsiveContainer width="100%" height={300}>
                        <AreaChart data={trend} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
                            <defs>
                                <linearGradient id="gI" x1="0" y1="0" x2="0" y2="1">
                                    <stop offset="0%"   stopColor={COLORS.income} stopOpacity={0.4} />
                                    <stop offset="100%" stopColor={COLORS.income} stopOpacity={0} />
                                </linearGradient>
                                <linearGradient id="gE" x1="0" y1="0" x2="0" y2="1">
                                    <stop offset="0%"   stopColor={COLORS.expenses} stopOpacity={0.3} />
                                    <stop offset="100%" stopColor={COLORS.expenses} stopOpacity={0} />
                                </linearGradient>
                            </defs>
                            <CartesianGrid stroke={COLORS.grid} strokeDasharray="3 3" vertical={false} />
                            <XAxis dataKey="label" stroke={COLORS.axis} tick={{ fontSize: 12 }} tickLine={false} axisLine={false} />
                            <YAxis stroke={COLORS.axis} tick={{ fontSize: 12 }} tickFormatter={compact} tickLine={false} axisLine={false} />
                            <Tooltip contentStyle={tooltipStyle} formatter={v => formatCurrency(v)} />
                            <Legend iconType="circle" wrapperStyle={{ fontSize: 12 }} />
                            <Area type="monotone" dataKey="income"   stroke={COLORS.income}   strokeWidth={2} fill="url(#gI)" name="Income" />
                            <Area type="monotone" dataKey="expenses" stroke={COLORS.expenses} strokeWidth={2} fill="url(#gE)" name="Expenses" />
                        </AreaChart>
                    </ResponsiveContainer>
                )}
            </Card>

            <Card title="Net profit" subtitle="Monthly net (income − expenses)">
                {trend.length === 0 ? <Empty msg="No data yet." /> : (
                    <ResponsiveContainer width="100%" height={220}>
                        <LineChart data={trend} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
                            <CartesianGrid stroke={COLORS.grid} strokeDasharray="3 3" vertical={false} />
                            <XAxis dataKey="label" stroke={COLORS.axis} tick={{ fontSize: 12 }} tickLine={false} axisLine={false} />
                            <YAxis stroke={COLORS.axis} tick={{ fontSize: 12 }} tickFormatter={compact} tickLine={false} axisLine={false} />
                            <Tooltip contentStyle={tooltipStyle} formatter={v => formatCurrency(v)} />
                            <Line type="monotone" dataKey="net" stroke={COLORS.net} strokeWidth={2} dot={{ r: 3, fill: COLORS.net }} activeDot={{ r: 5 }} name="Net" />
                        </LineChart>
                    </ResponsiveContainer>
                )}
            </Card>

            <Card title="Per-property P&L" subtitle="Last 6 months, sorted by net" padding={false}>
                <DataTable
                    density="compact"
                    columns={[
                        { key: 'name',     label: 'Property', sortable: true,
                          render: r => (
                              <span className="font-medium text-neutral-900">
                                  {r.house_number && <span className="text-primary-700 mr-1">#{r.house_number}</span>}
                                  {r.name}
                              </span>
                          ) },
                        { key: 'city',     label: 'City', sortable: true },
                        { key: 'income',   label: 'Income',   align: 'right', sortable: true, render: r => formatCurrency(r.income) },
                        { key: 'expenses', label: 'Expenses', align: 'right', sortable: true, render: r => <span className="text-danger-700">{formatCurrency(r.expenses)}</span> },
                        { key: 'net',      label: 'Net',      align: 'right', sortable: true,
                          render: r => <span className={`font-semibold ${r.net >= 0 ? 'text-success-700' : 'text-danger-700'}`}>{formatCurrency(r.net)}</span> },
                    ]}
                    rows={pnl}
                    rowKey={r => r.id}
                    pageSize={10}
                    emptyMessage="No properties with P&L data yet."
                />
            </Card>
        </div>
    );
};

/* ============================================================
 * TENANTS
 * ============================================================ */
const TenantsTab = () => {
    const [tenants, setTenants] = useState([]);
    useEffect(() => { axios.get('/api/reports/tenants').then(r => setTenants(r.data.tenants || [])).catch(() => toast.error('Failed')); }, []);
    const active = tenants.filter(t => t.status === 'active');
    const totalOutstanding = tenants.reduce((s,t) => s + Number(t.outstanding || 0), 0);
    const avgReliability = active.length ? Math.round(active.reduce((s,t) => s + Number(t.reliability_score || 0), 0) / active.length) : 0;
    const avgTenure = active.length ? Math.round(active.reduce((s,t) => s + Number(t.months_active || 0), 0) / active.length) : 0;

    return (
        <div className="space-y-6">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 sm:gap-4">
                <StatTile label="Active tenants"      value={active.length} icon={Users} accent="primary" />
                <StatTile label="Avg reliability"     value={`${avgReliability}%`} icon={ShieldCheck} accent="success" hint="0–100 (lateness score)" />
                <StatTile label="Avg tenure"          value={`${avgTenure} mo`} icon={Home} accent="primary" />
                <StatTile label="Total outstanding"   value={formatCurrency(totalOutstanding)} icon={AlertCircle} accent="warning" />
            </div>

            <Card title="All tenants" subtitle={`${tenants.length} record${tenants.length !== 1 ? 's' : ''}`} padding={false}>
                <DataTable
                    density="compact"
                    columns={[
                        { key: 'tenant_name', label: 'Tenant', sortable: true,
                          render: r => (
                              <div>
                                  <div className="font-medium text-neutral-900">{r.tenant_name}</div>
                                  <div className="text-xs text-neutral-500">{r.email}</div>
                              </div>
                          ) },
                        { key: 'property_name', label: 'Property', sortable: true,
                          render: r => <>{r.house_number && <span className="text-primary-700 mr-1">#{r.house_number}</span>}{r.property_name}</> },
                        { key: 'status', label: 'Status',
                          render: r => <Badge tone={r.status === 'active' ? 'success' : 'neutral'} dot>{r.status}</Badge> },
                        { key: 'months_active', label: 'Tenure', align: 'right', sortable: true, render: r => `${r.months_active || 0} mo` },
                        { key: 'monthly_rent', label: 'Rent',    align: 'right', sortable: true, render: r => formatCurrency(r.monthly_rent) },
                        { key: 'total_paid',   label: 'Paid',    align: 'right', sortable: true, render: r => formatCurrency(r.total_paid) },
                        { key: 'outstanding',  label: 'Owed',    align: 'right', sortable: true,
                          render: r => Number(r.outstanding) > 0
                              ? <span className="text-danger-700 font-medium">{formatCurrency(r.outstanding)}</span>
                              : <span className="text-neutral-400">—</span> },
                        { key: 'avg_days_late', label: 'Avg late', align: 'right', sortable: true, render: r => `${r.avg_days_late || 0}d` },
                        { key: 'reliability_score', label: 'Reliability', align: 'right', sortable: true,
                          render: r => <ReliabilityBar score={r.reliability_score} /> },
                    ]}
                    rows={tenants}
                    rowKey={r => r.tenant_id}
                    pageSize={20}
                    emptyMessage="No tenants yet."
                />
            </Card>
        </div>
    );
};

const ReliabilityBar = ({ score = 0 }) => {
    const color = score >= 80 ? 'bg-success-500' : score >= 60 ? 'bg-warning-500' : 'bg-danger-500';
    return (
        <div className="inline-flex items-center gap-2 min-w-[80px] justify-end">
            <div className="w-14 h-1.5 bg-neutral-100 rounded-full overflow-hidden">
                <div className={color} style={{ width: `${score}%`, height: '100%' }} />
            </div>
            <span className="tabular-nums text-xs font-medium text-neutral-700">{score}</span>
        </div>
    );
};

/* ============================================================
 * MANAGERS
 * ============================================================ */
const ManagersTab = () => {
    const [managers, setManagers] = useState([]);
    useEffect(() => { axios.get('/api/reports/managers').then(r => setManagers(r.data.managers || [])).catch(() => toast.error('Failed')); }, []);
    const totalCollected = managers.reduce((s,m) => s + Number(m.income_collected || 0), 0);
    const totalOpen      = managers.reduce((s,m) => s + Number(m.maintenance_open || 0), 0);

    return (
        <div className="space-y-6">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 sm:gap-4">
                <StatTile label="Managers"          value={managers.length} icon={UserCog} accent="primary" />
                <StatTile label="Collected this month" value={formatCurrency(totalCollected)} icon={DollarSign} accent="success" />
                <StatTile label="Open maintenance"  value={totalOpen} icon={Wrench} accent="warning" />
                <StatTile label="Properties managed"
                          value={managers.reduce((s,m) => s + Number(m.properties_managed || 0), 0)}
                          icon={Building2} accent="primary" />
            </div>

            <Card title="Manager performance" subtitle="This month's collection and operations" padding={false}>
                <DataTable
                    density="comfortable"
                    columns={[
                        { key: 'manager_name', label: 'Manager', sortable: true,
                          render: r => (
                              <div>
                                  <div className="font-medium text-neutral-900">{r.manager_name}</div>
                                  <div className="text-xs text-neutral-500">{r.email}</div>
                              </div>
                          ) },
                        { key: 'properties_managed', label: 'Properties', align: 'right', sortable: true },
                        { key: 'income_collected',   label: 'Income (MTD)', align: 'right', sortable: true,
                          render: r => <span className="font-medium text-success-700">{formatCurrency(r.income_collected)}</span> },
                        { key: 'expenses_incurred',  label: 'Expenses (MTD)', align: 'right', sortable: true,
                          render: r => <span className="text-danger-700">{formatCurrency(r.expenses_incurred)}</span> },
                        { key: 'maintenance_open',   label: 'Open jobs', align: 'right', sortable: true,
                          render: r => r.maintenance_open > 0 ? <Badge tone="warning">{r.maintenance_open}</Badge> : <span className="text-neutral-400">0</span> },
                        { key: 'maintenance_avg_days', label: 'Avg resolution', align: 'right', sortable: true,
                          render: r => r.maintenance_avg_days ? `${r.maintenance_avg_days} d` : '—' },
                    ]}
                    rows={managers}
                    rowKey={r => r.manager_id}
                    emptyMessage="No managers yet. Assign a manager to a property via Owners → Agreements."
                />
            </Card>
        </div>
    );
};

/* ============================================================
 * BUILDINGS
 * ============================================================ */
const BuildingsTab = () => {
    const [buildings, setBuildings] = useState([]);
    useEffect(() => { axios.get('/api/reports/buildings').then(r => setBuildings(r.data.buildings || [])).catch(() => toast.error('Failed')); }, []);

    return (
        <div className="space-y-6">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 sm:gap-4">
                <StatTile label="Buildings" value={buildings.length} icon={Building2} accent="primary" />
                <StatTile label="Total units"
                          value={buildings.reduce((s,b) => s + Number(b.units_count || 0), 0)}
                          icon={Home} accent="primary" />
                <StatTile label="Occupied"
                          value={buildings.reduce((s,b) => s + Number(b.occupied || 0), 0)}
                          icon={Home} accent="success" />
                <StatTile label="Potential income"
                          value={formatCurrency(buildings.reduce((s,b) => s + Number(b.potential_income || 0), 0))}
                          icon={TrendingUp} accent="primary" hint="/ month" />
            </div>

            <Card title="Per-building performance" padding={false}>
                <DataTable
                    columns={[
                        { key: 'name', label: 'Building', sortable: true,
                          render: r => <span className="font-medium text-neutral-900">{r.name}</span> },
                        { key: 'city', label: 'City', sortable: true },
                        { key: 'units_count', label: 'Units', align: 'right', sortable: true },
                        { key: 'occupied',    label: 'Occupied', align: 'right', sortable: true,
                          render: r => <>
                              {r.occupied || 0} <span className="text-xs text-neutral-400">/ {r.units_count || 0}</span>
                          </> },
                        { key: 'potential_income', label: 'Potential', align: 'right', sortable: true, render: r => formatCurrency(r.potential_income) },
                        { key: 'actual_income',    label: 'Actual',    align: 'right', sortable: true,
                          render: r => <span className="font-medium text-success-700">{formatCurrency(r.actual_income || 0)}</span> },
                        { key: 'income_6mo',       label: 'Income (6mo)', align: 'right', sortable: true, render: r => formatCurrency(r.income_6mo || 0) },
                        { key: 'expenses_6mo',     label: 'Exp (6mo)',    align: 'right', sortable: true,
                          render: r => <span className="text-danger-700">{formatCurrency(r.expenses_6mo || 0)}</span> },
                    ]}
                    rows={buildings}
                    rowKey={r => r.building_id || r.name}
                    emptyMessage="No buildings yet. Create one from the Buildings page."
                />
            </Card>
        </div>
    );
};

/* ============================================================
 * BALANCES
 * ============================================================ */
const BalancesTab = () => {
    const [data, setData] = useState(null);
    useEffect(() => { axios.get('/api/reports/balances').then(r => setData(r.data)).catch(() => toast.error('Failed')); }, []);
    if (!data) return <LoadingSkeleton />;
    return (
        <div className="space-y-6">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 sm:gap-4">
                <StatTile label="Total billed" value={formatCurrency(data.totals.total_billed)} accent="primary" />
                <StatTile label="Total paid"   value={formatCurrency(data.totals.total_paid)} accent="success" />
                <StatTile label="Outstanding"  value={formatCurrency(data.totals.outstanding)} accent="warning" icon={AlertCircle} />
                <StatTile label="Overdue"      value={formatCurrency(data.totals.overdue_amount)} accent="danger" icon={AlertCircle} />
            </div>

            <Card title="Balance per property" padding={false}>
                <DataTable
                    columns={[
                        { key: 'name', label: 'Property', sortable: true,
                          render: r => <span className="font-medium text-neutral-900">
                              {r.house_number && <span className="text-primary-700 mr-1">#{r.house_number}</span>}{r.name}
                          </span> },
                        { key: 'total_billed', label: 'Billed', align: 'right', sortable: true, render: r => formatCurrency(r.total_billed) },
                        { key: 'total_paid',   label: 'Paid',   align: 'right', sortable: true, render: r => formatCurrency(r.total_paid) },
                        { key: 'outstanding',  label: 'Outstanding', align: 'right', sortable: true,
                          render: r => Number(r.outstanding) > 0 ? <span className="text-warning-700 font-medium">{formatCurrency(r.outstanding)}</span> : <span className="text-success-700">Clear</span> },
                        { key: 'overdue_amount', label: 'Overdue', align: 'right', sortable: true,
                          render: r => Number(r.overdue_amount) > 0 ? <span className="text-danger-700 font-medium">{formatCurrency(r.overdue_amount)}</span> : '—' },
                    ]}
                    rows={data.properties}
                    rowKey={r => r.property_id}
                    emptyMessage="No balance data yet."
                />
            </Card>
        </div>
    );
};

/* ============================================================
 * UTILITIES
 * ============================================================ */
const UtilitiesTab = () => {
    const [d, setD] = useState(null);
    useEffect(() => { axios.get('/api/reports/utilities').then(r => setD(r.data)).catch(() => toast.error('Failed')); }, []);
    if (!d) return <LoadingSkeleton />;
    return (
        <div className="space-y-6">
            <div className="grid grid-cols-3 gap-4">
                <StatTile label="Billed to tenants"  value={formatCurrency(d.totals.billed)}       icon={Zap} accent="primary" />
                <StatTile label="Paid by tenants"    value={formatCurrency(d.totals.paid)}         accent="success" />
                <StatTile label="Landlord spend (12mo)" value={formatCurrency(d.totals.spend_last_12m)} accent="warning" />
            </div>

            <Card title="Utility spend — landlord expenses (12 months)">
                {d.expenses_series.length === 0 ? <Empty msg="No utility expenses logged." /> : (
                    <ResponsiveContainer width="100%" height={240}>
                        <BarChart data={d.expenses_series} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
                            <CartesianGrid stroke={COLORS.grid} strokeDasharray="3 3" vertical={false} />
                            <XAxis dataKey="label" stroke={COLORS.axis} tick={{ fontSize: 12 }} tickLine={false} axisLine={false} />
                            <YAxis stroke={COLORS.axis} tick={{ fontSize: 12 }} tickFormatter={compact} tickLine={false} axisLine={false} />
                            <Tooltip contentStyle={tooltipStyle} formatter={v => formatCurrency(v)} />
                            <Bar dataKey="spend" fill={COLORS.income} radius={[4,4,0,0]} name="Spend" />
                        </BarChart>
                    </ResponsiveContainer>
                )}
            </Card>

            <Card title="Bills by utility type" padding={false}>
                <DataTable
                    columns={[
                        { key: 'utility', label: 'Utility', sortable: true },
                        { key: 'bill_count', label: 'Bills', align: 'right', sortable: true },
                        { key: 'total_billed', label: 'Billed', align: 'right', sortable: true, render: r => formatCurrency(r.total_billed) },
                        { key: 'total_paid',   label: 'Paid',   align: 'right', sortable: true, render: r => <span className="text-success-700">{formatCurrency(r.total_paid)}</span> },
                        { key: 'outstanding',  label: 'Outstanding', align: 'right', sortable: true,
                          render: r => {
                              const out = Number(r.total_billed) - Number(r.total_paid);
                              return out > 0 ? <span className="text-warning-700 font-medium">{formatCurrency(out)}</span> : <span className="text-neutral-400">—</span>;
                          } },
                    ]}
                    rows={d.bills}
                    rowKey={r => r.utility_id}
                    emptyMessage="No utility bills yet."
                />
            </Card>
        </div>
    );
};

/* ============================================================
 * MAINTENANCE
 * ============================================================ */
const MaintenanceTab = () => {
    const [d, setD] = useState(null);
    useEffect(() => { axios.get('/api/reports/maintenance').then(r => setD(r.data)).catch(() => toast.error('Failed')); }, []);
    if (!d) return <LoadingSkeleton />;
    const totalReq = (d.by_status || []).reduce((s,r) => s + Number(r.n), 0);
    return (
        <div className="space-y-6">
            <div className="grid grid-cols-3 gap-4">
                <StatTile label="Total requests"   value={totalReq} icon={Wrench} accent="primary" />
                <StatTile label="Avg resolution"   value={d.avg_resolution_days ? `${d.avg_resolution_days} d` : '—'} accent="primary" />
                <StatTile label="Total cost"
                          value={formatCurrency((d.by_status || []).reduce((s,r) => s + Number(r.total_cost || 0), 0))}
                          accent="warning" />
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <Card title="By stage">
                    {(d.by_status || []).length === 0 ? <Empty msg="No requests yet." /> : (
                        <ResponsiveContainer width="100%" height={240}>
                            <BarChart data={d.by_status} layout="vertical" margin={{ left: 30 }}>
                                <CartesianGrid stroke={COLORS.grid} strokeDasharray="3 3" horizontal={false} />
                                <XAxis type="number" stroke={COLORS.axis} tick={{ fontSize: 12 }} allowDecimals={false} />
                                <YAxis type="category" dataKey="status" stroke={COLORS.axis} tick={{ fontSize: 12 }} width={100} />
                                <Tooltip contentStyle={tooltipStyle} />
                                <Bar dataKey="n" fill={COLORS.income} radius={[0,4,4,0]} name="Requests" />
                            </BarChart>
                        </ResponsiveContainer>
                    )}
                </Card>
                <Card title="By priority">
                    {(d.by_priority || []).length === 0 ? <Empty msg="No requests yet." /> : (
                        <ResponsiveContainer width="100%" height={240}>
                            <PieChart>
                                <Pie data={d.by_priority} dataKey="n" nameKey="priority" innerRadius={55} outerRadius={90} paddingAngle={2}>
                                    {d.by_priority.map((r, i) => (
                                        <Cell key={r.priority} fill={
                                            r.priority === 'urgent' ? COLORS.expenses :
                                            r.priority === 'high'   ? '#eab308' :
                                            r.priority === 'medium' ? COLORS.income : COLORS.available
                                        } />
                                    ))}
                                </Pie>
                                <Tooltip contentStyle={tooltipStyle} />
                                <Legend iconType="circle" wrapperStyle={{ fontSize: 12 }} />
                            </PieChart>
                        </ResponsiveContainer>
                    )}
                </Card>
            </div>
        </div>
    );
};

/* ---------------- shared ---------------- */
const tooltipStyle = { background: 'white', border: '1px solid #e2e8f0', borderRadius: 8, fontSize: 12, boxShadow: '0 4px 12px -2px rgba(15,23,42,0.06)' };
const Empty = ({ msg }) => <div className="text-neutral-400 text-sm py-10 text-center">{msg}</div>;
const LoadingSkeleton = () => (
    <div className="grid grid-cols-4 gap-4">{[1,2,3,4].map(i => <div key={i} className="h-24 bg-white rounded-xl border border-neutral-200/70 animate-pulse-soft" />)}</div>
);

export default Reports;
